import { useEffect, useRef, useState, type ReactElement } from "react";
import { TextInput } from "../../ui";
import type { LeadTarget } from "./types";
import styles from "./RecordAutocomplete.module.css";

interface RecordAutocompleteProps {
  label: string;
  placeholder: string;
  selected: LeadTarget | null;
  search: (query: string) => Promise<LeadTarget[]>;
  onSelect: (target: LeadTarget) => void;
  onClear: () => void;
}

export function RecordAutocomplete({
  label,
  placeholder,
  selected,
  search,
  onSelect,
  onClear,
}: RecordAutocompleteProps): ReactElement {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<LeadTarget[] | null>(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected) return;
    let live = true;
    const timer = setTimeout(async () => {
      try {
        const found = await search(query);
        if (live) {
          setResults(found);
          setFailed(false);
        }
      } catch {
        if (live) setFailed(true);
      }
    }, 200);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, search, selected]);

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  if (selected) {
    return (
      <div className={styles.field}>
        <span className={styles.label}>{label}</span>
        <div className={styles.chosen}>
          <span className={styles.chosenLabel}>{selected.label}</span>
          <span className={styles.meta}>{selected.meta}</span>
          <button
            type="button"
            className={styles.change}
            onClick={() => {
              setQuery("");
              setOpen(true);
              onClear();
            }}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.field} ref={wrapRef}>
      <span className={styles.label}>{label}</span>
      <div className={styles.wrap}>
        <TextInput
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onChange={(event) => setQuery(event.target.value)}
        />
        {open && (failed || results) ? (
          <div className={styles.panel}>
            {failed ? (
              <p className={styles.note}>
                Could not search this sandbox. Try again in a moment.
              </p>
            ) : results && results.length === 0 ? (
              <p className={styles.note}>
                {query
                  ? "Nothing in this sandbox matches. Clear the search to see all."
                  : "This sandbox has none yet. Seeded records appear here once it has them."}
              </p>
            ) : (
              results?.map((target) => (
                <button
                  key={target.id}
                  type="button"
                  className={styles.option}
                  onClick={() => {
                    setOpen(false);
                    onSelect(target);
                  }}
                >
                  <span className={styles.optionLabel}>{target.label}</span>
                  <span className={styles.meta}>{target.meta}</span>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
